(ns frontend.view.components.filter
  (:require
   [reagent.core :as r]
   [common.localization :as loc]
   [frontend.bootstrap.components :as rbs]
   [potpuri.core :refer [index-by fn-> fn->>]]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.util :as cu]
   [frontend.utils :as fu]
   [frontend.state.current-session :as session]
   [common.schema.registry :as registry]
   [common.attribute :as attribute]
   [frontend.state.association :as association]
   [common.schema.data :as schema-data]))

(defn contact-country-filter
  [filter-data entity]
  (let [person-associations (association/resolve-related-entities-by-group entity
                                                                           :entity.type/person)]
    (and
     entity
     (->> person-associations
          (mapcat (fn [[association-type associations]]
                    (when (some #{association-type} (:selected-association-types filter-data))
                      associations)))
          (remove nil?)
          (keep (fn [association]
                  (let [person (let [source (:entity-association/target-entity association)
                                     target (:entity-association/source-entity association)]
                                 (if (= :entity.type/person
                                        (:entity/type (get @session/entities source)))
                                   (get @session/entities source)
                                   (get @session/entities target)))
                        country-location (:person/location-country person)
                        city-location (:person/location-city person)
                        selected-countries (:selected-countries filter-data)
                        selected-cities (:selected-cities filter-data)]
                    (when (and (some #{country-location} selected-countries)
                               (or (empty? selected-cities)
                                   (some #{city-location} selected-cities)))
                      person))))
          (seq)))))

(def custom-filter-functions
  {:custom-filter-fundraising-investor-contact-country contact-country-filter
   :custom-filter-sales-investor-contact-country       contact-country-filter})

;;localization keyword from operand
;; e.g: :operand/before => :operand-filter/before
(defn operand-loc-kw
  [operand]
  (keyword (str (namespace operand) "-filter") (name operand)))

;;vals is a map where keys are :db/id and values are display-strings
;; (e.g. for accounts: {123123 "Firstname Lastname"}
(defn display-multiple
  [separator vals]
  (let [commafy (->> (butlast vals)
                     (map second)
                     (string/join ", "))
        final (second (last vals))]
    (if (< (count vals) 2)
      final
      (or (str commafy
               " " separator
               " "
               final)))))

(def display-or (partial display-multiple (loc/tr :or)))
(def display-and (partial display-multiple (loc/tr :and)))

(defn display-entity
  [EID]
  (some-> (session/get-entity EID)
          (entity/resolve-entity-name session/get-entity session/reverse-lookup)))

(defn display-date
  [ms]
  (session/long->tenant-date ms))

(defn display-datetime
  [ms]
  (session/long->tenant-date ms))


(defn get-filter-display-data
  [field-schema filter-data]
  (let [is-sequential-data-type? (some #{(:field/data-type field-schema)}
                                       [:data.type/ref-multi
                                        :data.type/email-multi
                                        :data.type/keyword-multi
                                        :data.type/text-multi
                                        :data.type/url-multi])
        value-to-coerce (if is-sequential-data-type?
                          (if (sequential? filter-data)
                            filter-data
                            [filter-data])
                          filter-data)]
    (cond
      is-sequential-data-type?
      (schema-data/coerce-to-str field-schema
                                 value-to-coerce
                                 nil
                                 @session/tenant
                                 session/get-entity
                                 nil)
      (sequential? value-to-coerce)
      (->> value-to-coerce
           (map (fn [child-value]
                  (schema-data/coerce-to-str field-schema
                                             child-value
                                             nil
                                             @session/tenant
                                             session/get-entity
                                             nil)))
           (string/join ", "))
      :else
      (schema-data/coerce-to-str field-schema
                                 value-to-coerce
                                 nil
                                 @session/tenant
                                 session/get-entity
                                 nil))))


(defn simple-display-item
  [{:keys [get-column-label]} [filter-key filter-data]]
  (when (and filter-key filter-data)
    (let [{:keys [operand lower-bound-value upper-bound-value label]} filter-data
          tenant @session/tenant
          tenant-schema @session/tenant-schema
          field-name (or (when (fn? get-column-label)
                           (get-column-label filter-key))
                         (attribute/attribute-label tenant-schema filter-key))
          lower-bound-value (cond (= operand :operand/or)
                                  (display-or lower-bound-value)
                                  (= operand :operand/and)
                                  (display-and lower-bound-value)
                                  :default lower-bound-value)
          field-schema (registry/get-field-schema tenant-schema filter-key)
          loc-kw (when operand
                   (operand-loc-kw operand))
          label-builder (when operand
                          (partial get-filter-display-data field-schema))]
      (condp = operand
        :operand/all-of
        (str (loc/tr :operand-filter/all-of [field-name])
             (loc/tr :operand-filter/all-of-emphasize)
             (loc/tr :operand-filter/all-of-list
                 [(string/join ", "
                               (->> lower-bound-value
                                    (rest)
                                    (map label-builder)))])
             (when (not-empty (rest lower-bound-value)) (loc/tr :operand-filter/all-of-list-operand))
             (loc/tr :operand-filter/all-of-list-last [(label-builder (first lower-bound-value))]))
        :operand/any-of
        (str (loc/tr :operand-filter/any-of [field-name])
             (loc/tr :operand-filter/any-of-emphasize)
             (loc/tr :operand-filter/any-of-list
                 [(string/join ", "
                               (->> lower-bound-value
                                    (rest)
                                    (map label-builder)))])
             (when (not-empty (rest lower-bound-value)) (loc/tr :operand-filter/any-of-list-operand))
             (loc/tr :operand-filter/any-of-list-last [(label-builder (first lower-bound-value))]))
        :operand/none-of
        (str (loc/tr :operand-filter/none-of [field-name])
             (loc/tr :operand-filter/none-of-emphasize)
             (loc/tr :operand-filter/none-of-list
                 [(string/join ", "
                               (->> lower-bound-value
                                    (rest)
                                    (map label-builder)))])
             (when (not-empty (rest lower-bound-value)) (loc/tr :operand-filter/none-of-list-operand))
             (loc/tr :operand-filter/none-of-list-last [(label-builder (first lower-bound-value))]))
        (if (get custom-filter-functions filter-key)
          label
          (loc/tr loc-kw
              [field-name
               (label-builder lower-bound-value)
               (label-builder upper-bound-value)]))))))

(defn simple-display
  [args [filter-key filter-data]]
  (if-not (= :filter.type/or-combination (:filter/type filter-data))
    (simple-display-item args [filter-key filter-data])
    (->> filter-data
         :filter/child-filters
         (keep (fn [child-filter]
                 (let [r (simple-display-item args child-filter)]
                   r)))
         (string/join (loc/tr :or-separator)))))

(defn view
  [{:keys [filter get-column-label set-filter! edit-filter!]}]
  (let [[column-key filter-data] filter]
    [rbs/Badge
     {:class-name (fu/cs :tags-label :tags-label__green) ;;TODO use filter specific class-names if necessary
      :on-click   #(edit-filter! column-key filter-data)}

     ;;TODO when necessary, we can e.g. use multimethods based on column-key or
     ;; operator type to handle displaying different types of filters
     (simple-display {:get-column-label get-column-label} filter)
     [:span.filter-close
      [:i.fal.fa-times
       {:on-click (fn [e]
                    (.preventDefault e)
                    (.stopPropagation e)
                    (set-filter! column-key nil))}]]]))
